/**
 * Setting the tablet up for a robot, and freeing it again. One app build serves every robot: the
 * installer enters a service code, picks the hospital, then one of its robots that has no tablet
 * yet (the server never offers a robot twice). Both need data.
 */
import type { EnrollTabletResponse, SetupHospitalDto, SetupResponse } from '@evesix/contracts';
import { ApiRequestError, type ApiClient } from './api';
import type { DirectRobotGateway } from './direct-robot';
import type { LocalStore } from './local-store';
import type { SyncService } from './sync';

/** Remembered per robot: none of it may carry over to the next one. */
const ROBOT_SPECIFIC_KEYS = ['switch.address', 'signin.failures'];

export type SetupProblem =
  | { readonly reason: 'invalid' }
  | { readonly reason: 'locked'; readonly retryAfterSeconds: number }
  | { readonly reason: 'no_data' };

export type SetupStart =
  | {
      readonly ok: true;
      readonly token: string;
      readonly operatorName: string;
      readonly hospitals: readonly SetupHospitalDto[];
    }
  | ({ readonly ok: false } & SetupProblem);

/** What the tablet saved for working with no data, for the installer to check. */
export interface SyncedPack {
  readonly robotId: string;
  readonly hospitalName: string;
  readonly wards: number;
  readonly beds: number;
  /** The hospital's operators (service accounts not counted). */
  readonly operators: number;
}

export type SetupFinish =
  | {
      readonly ok: true;
      /** Null when the data dropped right after setup: the pack downloads at the next sync. */
      readonly synced: SyncedPack | null;
    }
  | {
      readonly ok: false;
      /** taken: another tablet got the robot meanwhile; expired: enter the code again. */
      readonly reason: 'taken' | 'expired' | 'not_found' | 'no_data';
    };

export type ResetResult =
  | { readonly ok: true }
  | ({ readonly ok: false } & SetupProblem)
  | {
      readonly ok: false;
      /** waiting: records not uploaded yet; cycle_running: a cycle is on. */
      readonly reason: 'waiting' | 'cycle_running';
    };

/** What the setup screen needs; the tests and the app use the class below. */
export interface TabletSetup {
  start(pin: string): Promise<SetupStart>;
  finish(token: string, robotId: string): Promise<SetupFinish>;
  reset(pin: string): Promise<ResetResult>;
}

export interface TabletSetupOptions {
  /** Unauthenticated: setup carries its own short-lived token. */
  readonly client: ApiClient;
  readonly sync: SyncService;
  readonly store: LocalStore;
  readonly robot: DirectRobotGateway;
  readonly appVersion: string;
}

export class TabletSetupService implements TabletSetup {
  readonly #client: ApiClient;
  readonly #sync: SyncService;
  readonly #store: LocalStore;
  readonly #robot: DirectRobotGateway;
  readonly #appVersion: string;

  constructor({ client, sync, store, robot, appVersion }: TabletSetupOptions) {
    this.#client = client;
    this.#sync = sync;
    this.#store = store;
    this.#robot = robot;
    this.#appVersion = appVersion;
  }

  /** Checks the service code; answers with the hospitals and their robots free to choose. */
  async start(pin: string): Promise<SetupStart> {
    try {
      const res = await this.#client.post<SetupResponse>('/setup', { pin });
      return {
        ok: true,
        token: res.token,
        operatorName: res.operatorName,
        hospitals: res.hospitals,
      };
    } catch (error) {
      return { ok: false, ...problemOf(error) };
    }
  }

  /** Sets the tablet up for the robot chosen, and fetches what it needs to work with no data. */
  async finish(token: string, robotId: string): Promise<SetupFinish> {
    let enrolled: EnrollTabletResponse;
    try {
      enrolled = await this.#client.post<EnrollTabletResponse>('/tablets/setup', {
        token,
        robotId,
        appVersion: this.#appVersion,
      });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.code === 'robot_taken') return { ok: false, reason: 'taken' };
        if (error.status === 401) return { ok: false, reason: 'expired' };
        if (error.status === 404) return { ok: false, reason: 'not_found' };
      }
      return { ok: false, reason: 'no_data' };
    }
    await Promise.all(ROBOT_SPECIFIC_KEYS.map((key) => this.#store.remove(key)));
    await this.#sync.adopt(enrolled.token, robotId);
    const pack = this.#sync.pack;
    return {
      ok: true,
      synced: pack && {
        robotId: pack.robot.id,
        hospitalName: pack.robot.hospitalName,
        wards: pack.wards.length,
        beds: pack.wards.reduce((sum, w) => sum + w.beds, 0),
        operators: pack.operators.filter((o) => !o.service).length,
      },
    };
  }

  /**
   * Frees the robot so this tablet can be set up for another (or another tablet for this robot).
   * Only with a service code, with data, no cycle on, and nothing left to upload: a reset must
   * never lose a record.
   */
  async reset(pin: string): Promise<ResetResult> {
    await Promise.all([this.#sync.ready, this.#robot.ready]);
    if (this.#robot.activeRun()) return { ok: false, reason: 'cycle_running' };
    await this.#sync.syncNow();
    if (this.#sync.status().pending > 0) return { ok: false, reason: 'waiting' };
    const check = await this.start(pin);
    if (!check.ok) return check;
    try {
      await this.#sync.release();
    } catch {
      return { ok: false, reason: 'no_data' };
    }
    await Promise.all(ROBOT_SPECIFIC_KEYS.map((key) => this.#store.remove(key)));
    return { ok: true };
  }
}

function problemOf(error: unknown): SetupProblem {
  if (error instanceof ApiRequestError) {
    if (error.code === 'invalid_pin' || error.status === 400) return { reason: 'invalid' };
    if (error.code === 'locked') {
      return { reason: 'locked', retryAfterSeconds: error.retryAfterSeconds ?? 900 };
    }
  }
  return { reason: 'no_data' };
}
